import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Loader2, AlertTriangle } from "lucide-react";
import { MarketingLayout } from "@/components/MarketingLayout";
import { CustomerGate } from "@/components/customer/CustomerGate";
import { AmazonReportText } from "@/components/customer/AmazonReportText";
import { TakedownStatusControls } from "@/components/customer/TakedownStatusControls";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { track } from "@/lib/analytics";

type ActionStatus = "none" | "watching" | "authorized" | "submitted" | "removed" | "rejected";
type TakedownCapability =
  | "unknown"
  | "unverified_no_brand_registry"
  | "brand_registry_access_required"
  | "brand_registry_access_pending"
  | "brand_registry_ready";
type Violation = {
  type: string;
  confidence: number;
  policyClause?: string;
  evidence?: string;
};

type Review = {
  id: string;
  asin: string;
  amazonReviewId?: string;
  reviewUrl?: string | null;
  rating: number;
  title: string | null;
  body: string;
  reviewerName: string | null;
  verifiedPurchase: boolean;
  takedownCapability: TakedownCapability;
  accessStatus: "none" | "invite_sent" | "verified";
  brand: {
    id: string;
    name: string;
    brandRegistry: boolean;
    brandRegistryAnsweredAt: string | null;
    collaboratorStatus: "none" | "invite_sent" | "verified";
  } | null;
  classification: {
    flaggedForTakedown: boolean;
    violations: Violation[];
    rationale: string | null;
    takedownRequest: string | null;
    actionStatus: ActionStatus;
  } | null;
};

type AuditDetail = {
  id: string;
  email: string;
  inputAsins: string[];
  scrapedReviewCount: number;
  flaggedCount: number;
  createdAt: string;
  reviews: Review[];
};

type StatusUpdate = {
  actionStatus: ActionStatus;
  submittedAt: string | null;
  removedAt: string | null;
  chargeStatus?: string | null;
};

const VIOLATION_LABELS: Record<string, string> = {
  seller_feedback_misplaced: "Off-topic seller feedback",
  conflict_of_interest: "Conflict of interest",
  incentivized: "Incentivized review",
  profanity_or_harassment: "Profanity / harassment",
  privacy_violation: "Privacy violation",
  off_topic: "Off-topic",
  duplicate_or_template: "Duplicate / template",
  manufacturer_misrepresentation: "Manufacturer misrepresentation",
  competitor_review: "Competitor review",
  review_swap: "Review swap",
};

const PILL_STYLES: Record<
  ActionStatus | "pending" | "clean" | "flagged",
  { cls: string; label: string }
> = {
  pending: { cls: "bg-slate-100 text-slate-700 ring-slate-200", label: "Pending" },
  clean: { cls: "bg-emerald-50 text-emerald-700 ring-emerald-200", label: "Clean" },
  flagged: { cls: "bg-amber-50 text-amber-800 ring-amber-200", label: "Flagged" },
  none: { cls: "bg-slate-100 text-slate-700 ring-slate-200", label: "Pending" },
  watching: { cls: "bg-slate-100 text-slate-700 ring-slate-200", label: "Watching" },
  authorized: { cls: "bg-sky-50 text-sky-700 ring-sky-200", label: "Authorized · awaiting filing" },
  submitted: { cls: "bg-blue-50 text-blue-700 ring-blue-200", label: "Submitted" },
  removed: { cls: "bg-emerald-100 text-emerald-800 ring-emerald-300", label: "Removed" },
  rejected: { cls: "bg-red-50 text-red-700 ring-red-200", label: "Rejected" },
};

function verdictOf(r: Review): keyof typeof PILL_STYLES {
  if (!r.classification) return "pending";
  const a = r.classification.actionStatus;
  if (a === "removed") return "removed";
  if (a === "submitted") return "submitted";
  if (a === "authorized") return "authorized";
  if (a === "watching") return "watching";
  if (a === "rejected") return "rejected";
  return r.classification.flaggedForTakedown ? "flagged" : "clean";
}

function StatusPill({ verdict }: { verdict: keyof typeof PILL_STYLES }) {
  const { cls, label } = PILL_STYLES[verdict];
  return (
    <span
      className={`inline-flex shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium ring-1 ring-inset ${cls}`}
    >
      {label}
    </span>
  );
}

function AuthorizeButton({
  reviewId,
  brandId,
  canAuthorize,
  reason,
  onChange,
}: {
  reviewId: string;
  brandId: string | null;
  canAuthorize: boolean;
  reason: string | null;
  onChange: () => void;
}) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onClick() {
    if (!brandId) return;
    if (!window.confirm("Authorize this takedown submission?")) {
      return;
    }
    setError(null);
    setSubmitting(true);
    const res = await api(`/customer/reviews/${reviewId}/authorize-takedown`, {
      method: "POST",
      body: JSON.stringify({ brandId }),
    });
    setSubmitting(false);
    if (!res.ok) {
      setError(res.error ?? "Could not authorize the takedown.");
      return;
    }
    track("takedown_authorized", { review_id: reviewId });
    onChange();
  }

  if (!canAuthorize) {
    return (
      <div className="text-xs text-slate-500">
        {reason ?? "Cannot authorize yet."}
      </div>
    );
  }

  return (
    <div>
      <Button size="sm" onClick={onClick} disabled={submitting}>
        {submitting && <Loader2 className="h-3 w-3 animate-spin" />}
        {submitting ? "Authorizing…" : "Authorize takedown"}
      </Button>
      {error && (
        <p className="mt-2 text-xs text-destructive" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

function Inner({ auditId }: { auditId: string }) {
  const [audit, setAudit] = useState<AuditDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  function applyStatus(reviewId: string, next: StatusUpdate) {
    setAudit((prev) =>
      prev
        ? {
            ...prev,
            reviews: prev.reviews.map((review) =>
              review.id === reviewId && review.classification
                ? {
                    ...review,
                    classification: {
                      ...review.classification,
                      actionStatus: next.actionStatus,
                    },
                  }
                : review
            ),
          }
        : prev
    );
  }

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      api<AuditDetail>(`/customer/audits/${auditId}`),
    ]).then(([auditRes]) => {
      if (cancelled) return;
      if (!auditRes.ok) {
        setError(auditRes.error ?? "Audit not found.");
        return;
      }
      setAudit(auditRes.data ?? null);
    });
    return () => {
      cancelled = true;
    };
  }, [auditId, reloadKey]);

  if (error) {
    return (
      <div className="mx-auto w-full max-w-3xl px-6 py-12">
        <div className="flex items-center gap-2 text-sm text-destructive">
          <AlertTriangle className="h-4 w-4" />
          {error}
        </div>
      </div>
    );
  }

  if (!audit) {
    return (
      <div className="mx-auto w-full max-w-3xl px-6 py-12">
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      </div>
    );
  }

  const unverifiedFlaggedReview = audit.reviews.find(
    (review) =>
      review.classification?.flaggedForTakedown &&
      review.takedownCapability !== "brand_registry_ready"
  );

  return (
    <div className="mx-auto w-full max-w-3xl px-6 py-12">
      <Link to="/dashboard" className="text-sm text-slate-500 hover:text-slate-900">
        ← All audits
      </Link>

      <h1 className="mt-4 text-3xl font-semibold tracking-tight text-slate-900">
        Audit {audit.id.slice(0, 8)}
      </h1>
      <p className="mt-2 text-sm text-slate-600">
        {audit.scrapedReviewCount} reviews · {audit.flaggedCount} flagged
      </p>

      {unverifiedFlaggedReview && (
        <div className="mt-6 rounded-md border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          <p className="font-medium">
            Managed takedowns are not ready for every brand
          </p>
          <ul className="mt-2 list-inside list-disc space-y-1 text-xs">
            <li>
              <Link
                to={
                  unverifiedFlaggedReview.brand
                    ? `/dashboard/onboarding?brand=${encodeURIComponent(unverifiedFlaggedReview.brand.id)}`
                    : "/dashboard/onboarding"
                }
                className="underline underline-offset-4 hover:text-amber-950"
              >
                Review {unverifiedFlaggedReview.brand?.name ?? "your brand"}&rsquo;s Amazon setup
              </Link>{" "}
              for its current monitoring and takedown capability.
            </li>
          </ul>
        </div>
      )}

      <ul className="mt-8 space-y-3">
        {audit.reviews.map((r) => {
          const verdict = verdictOf(r);
          const violations = r.classification?.violations ?? [];
          const flagged = r.classification?.flaggedForTakedown;
          const action = r.classification?.actionStatus ?? "none";
          const takedownsReady =
            r.takedownCapability === "brand_registry_ready";
          const canAuthorize =
            flagged &&
            takedownsReady &&
            (action === "none" || action === "watching" || action === "rejected");
          let reason: string | null = null;
          if (flagged && !canAuthorize) {
            if (action === "authorized") reason = "Authorized — waiting for Reputely to file it through Brand Registry Support.";
            else if (action === "submitted") reason = "Submitted to Amazon — awaiting their response.";
            else if (action === "removed") reason = "Already removed by Amazon.";
            else if (!takedownsReady) {
              reason =
                r.takedownCapability === "unverified_no_brand_registry"
                  ? "Managed takedowns are not yet supported for this brand without Brand Registry."
                  : "Complete Brand Registry setup first.";
            }
          }

          return (
            <li
              key={r.id}
              className="rounded-xl border border-slate-200 bg-white p-5"
            >
              <div className="flex flex-wrap items-center gap-2 text-xs text-slate-600">
                <StatusPill verdict={verdict} />
                <span className="font-mono">{r.asin}</span>
                <span>·</span>
                <span>{r.rating}★</span>
                {r.verifiedPurchase && (
                  <>
                    <span>·</span>
                    <span className="text-emerald-700">Verified</span>
                  </>
                )}
              </div>

              {r.title && (
                <div className="mt-2 font-medium text-slate-900">{r.title}</div>
              )}
              <p className="mt-2 whitespace-pre-wrap text-sm text-slate-700">
                {r.body}
              </p>

              {flagged && violations.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {violations.map((v, i) => (
                    <span
                      key={i}
                      className="inline-flex rounded-full bg-red-50 px-2 py-0.5 text-xs font-medium text-red-700 ring-1 ring-inset ring-red-200"
                    >
                      {VIOLATION_LABELS[v.type] ?? v.type}
                    </span>
                  ))}
                </div>
              )}

              {flagged && r.classification && takedownsReady && (
                <AmazonReportText review={r} />
              )}

              {flagged && (
                <div className="mt-4">
                  <AuthorizeButton
                    reviewId={r.id}
                    brandId={r.brand?.id ?? null}
                    canAuthorize={Boolean(canAuthorize)}
                    reason={reason}
                    onChange={reload}
                  />
                  {r.classification &&
                    r.brand?.id &&
                    r.classification.actionStatus === "authorized" && (
                    <TakedownStatusControls
                      reviewId={r.id}
                      actionStatus={r.classification.actionStatus}
                      onChange={(next) =>
                        applyStatus(r.id, {
                          ...next,
                          actionStatus: next.actionStatus as ActionStatus,
                        })
                      }
                      className="mt-3"
                    />
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export default function CustomerAuditDetail() {
  const { id } = useParams<{ id: string }>();
  if (!id) return null;
  return (
    <MarketingLayout>
      <CustomerGate>
        {() => <Inner auditId={id} />}
      </CustomerGate>
    </MarketingLayout>
  );
}
